// Preliminary checks on an ordered property (common/checks.py in the API): has each check
// passed, raised a warning, is it still running, or has it failed. The wording comes from the
// API and never names a data source.
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { PreliminaryCheck } from "../api";

const ICONS = {
  passed: CheckCircle2,
  warning: AlertTriangle,
  checking: Loader2,
  failed: XCircle,
};

const SUMMARY: Record<string, string> = {
  passed: "All checks passed",
  warning: "Passed, with something to note",
  checking: "Checks still running",
  failed: "A check failed",
};

/** The overall state: failed beats checking beats warning beats passed. */
function overall(checks: PreliminaryCheck[]) {
  for (const s of ["failed", "checking", "warning"] as const) if (checks.some((c) => c.status === s)) return s;
  return "passed";
}

export function PreliminaryChecks({ checks }: { checks: PreliminaryCheck[] }) {
  if (!checks.length) return null;
  const state = overall(checks);
  const Summary = ICONS[state];
  return (
    <section className="card prechecks" aria-label="Preliminary checks">
      <div className="prechecks-head">
        <h2 className="building-h2">Preliminary checks</h2>
        <span className={`prechecks-summary is-${state}`}>
          <Summary size={14} aria-hidden="true" className={state === "checking" ? "spin" : undefined} /> {SUMMARY[state]}
        </span>
      </div>
      <ul className="prechecks-list">
        {checks.map((c) => {
          const Icon = ICONS[c.status];
          return (
            <li key={c.key} className={`is-${c.status}`}>
              <Icon size={18} aria-hidden="true" className={`prechecks-icon${c.status === "checking" ? " spin" : ""}`} />
              <div>
                <b>{c.label}</b>
                <span className="muted">{c.detail}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
